import React, { useState, useEffect, useMemo } from 'react';

import { User, TrendingUp, Globe, Smartphone, Send, ArrowLeft, HomeIcon } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { message } from "antd";

import { gql } from '@apollo/client';
import backIcon from "../../Images/backIcon.png";
import homeIcon from "../../Images/homeIcon.png";
import { GET_USER_BEHAVIOURAL_INSIGHTS, Get_User_Device_Details, Get_User_Profile, CREATE_NEW_WEB_NOTIFICATION } from "../../services/userAPI";

import UserProfileIcon from "../../Images/userProfileUserUnderstandIcon.png";

// GraphQL Query to fetch behavioral insights


const ClanCrewUnderstandUserProfile = ({ user: userProp }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { userId: userIdParam } = useParams(); // userId from URL
    const userFromState = location.state || {};

    const userId = parseInt(userIdParam) || userFromState?.userId;

    const name = userFromState?.name || "";
    const accountName = userFromState?.accountName || "";
    const accountId = userFromState?.accountId || localStorage.getItem("accountId");

    const [showNotificationModal, setShowNotificationModal] = useState(false);
    const [sendingNotification, setSendingNotification] = useState(false);
    const [notificationSent, setNotificationSent] = useState(false);
    const [notifyUser, setNotifyUser] = useState(true);
    const [notifyManager, setNotifyManager] = useState(false);
    const [userTitle, setUserTitle] = useState("");
    const [userDescription, setUserDescription] = useState("");
    const [managerTitle, setManagerTitle] = useState("");
    const [managerDescription, setManagerDescription] = useState("");

    // Fetch behavioral insights
    const [fetchInsights, { data: insightsData, loading: insightsLoading, error: insightsError }] =
        useLazyQuery(GET_USER_BEHAVIOURAL_INSIGHTS, {
            variables: { userId },
            fetchPolicy: "network-only",
        });

    useEffect(() => {
        if (userId) {
            fetchInsights();
        }
    }, [userId, fetchInsights]);

    const { data: deviceData, loading: deviceLoading, error: deviceError, refetch: refetchDevice } = useQuery(
        Get_User_Device_Details,
        {
            variables: { userId },
            fetchPolicy: "network-only",
            skip: !userId,
        }
    );

    const { data: profileData } = useQuery(
        Get_User_Profile,
        {
            variables: { userId },
            fetchPolicy: "cache-and-network",
            skip: !userId,
        }
    );

    // Transform API response into table-friendly format
    const behaviouralInsights = useMemo(() => {
        if (!insightsData?.getQuestionsAnsweredForUser) return [];

        return insightsData.getQuestionsAnsweredForUser.map((item) => {
            let answerText = "No response";
            let whatItConveys = "";
            let recommendedAction = "";

            if (item.__typename === "OnboardingQuestion") {
                const selectedOption = item.options?.find(opt => opt.id === item.responce?.optionId);
                answerText = selectedOption?.optionText || "";
                // Use server-provided behavioural metadata when available
                whatItConveys = selectedOption?.whatItConveys || "";
                recommendedAction = selectedOption?.recommendedActionToManager || "";
            }

            if (item.__typename === "UserPersonaQuestion") {
                const selectedAnswer = item.answers?.find(ans =>
                    item.responses?.some(resp => resp.answerId === ans.id)
                );
                answerText = selectedAnswer?.answerText || "";
                // Use server-provided behavioural metadata when available
                whatItConveys = selectedAnswer?.whatItConveys || "";
                recommendedAction = selectedAnswer?.recommendedActionToManager || "";
            }

            return {
                question: item.question,
                answer: answerText,
                whatItConveys,
                recommendedAction,
            };
        });
    }, [insightsData]);

    const state = location.state || {};
    const appLanguageName = state.appLanguage || "";
    const videoLanguage = Array.isArray(state.preferredLanguages) && state.preferredLanguages.length > 0
        ? state.preferredLanguages.map(l => l.name || l.language || l.code).join(", ")
        : "";
    const employeeId = state.employeeId || "";

    // 1. AGE CALCULATION
    const calculateAge = (dob) => {
        if (!dob) return "";
        const birth = new Date(dob);
        const today = new Date();
        let age = today.getFullYear() - birth.getFullYear();
        const m = today.getMonth() - birth.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
        return age <= 0 ? "Less than 1 year" : `${age} year${age > 1 ? "s" : ""}`;
    };

    // 2. TENURE WITH DAYS (exactly like your screenshot)
    const calculateTenure = (joinDate) => {
        if (!joinDate) return "";
        const join = new Date(joinDate);
        const now = new Date();

        let years = now.getFullYear() - join.getFullYear();
        let months = now.getMonth() - join.getMonth();
        let days = now.getDate() - join.getDate();

        if (days < 0) {
            months--;
            const prevMonth = new Date(now.getFullYear(), now.getMonth(), 0);
            days += prevMonth.getDate();
        }
        if (months < 0) {
            years--;
            months += 12;
        }

        const parts = [];
        if (years > 0) parts.push(`${years} year${years > 1 ? "s" : ""}`);
        if (months > 0) parts.push(`${months} month${months > 1 ? "s" : ""}`);
        if (days > 0 || parts.length === 0) parts.push(`${days} day${days > 1 ? "s" : ""}`);

        return parts.join(" ");
    };

    const age = calculateAge(state.dateOfBirth);
    const tenure = calculateTenure(state.joiningDate);
    const managerName = state.manager || "";
    const hasManager = !!managerName && managerName !== "Not Assigned";

    // Fallback user data if API fails or not loaded
    const user = {
        name: `${userFromState.name || ""} ${userFromState.surname || ""}`.trim() || "",
        profilePicture: userFromState.profilePicture || UserProfileIcon,
        tenure: calculateTenure(userFromState.joiningDate),
        manager: userFromState.reporting?.name || "",
        performanceResidency: userFromState.performanceResidency || profileData?.userProfile?.performanceResidency || { performance: "", residency: "", color: "" },
        preferredLanguages: userFromState.preferredLanguages || [],
        behaviouralInsights
    };

    const normalizeColor = (c) => {
        if (!c) return "#9CA3AF"; // gray-400 for unknown
        let hex = typeof c === "string" ? c.trim() : String(c);
        if (!hex) return "#9CA3AF";
        if (hex.startsWith('#')) hex = hex.slice(1);
        if (hex.toLowerCase().startsWith('0x')) hex = hex.slice(2);
        if (hex.length === 8) hex = hex.slice(2); // AARRGGBB -> RRGGBB
        if (hex.length === 6) {
            // Check if color is too light (white or near-white)
            const r = parseInt(hex.substring(0, 2), 16);
            const g = parseInt(hex.substring(2, 4), 16);
            const b = parseInt(hex.substring(4, 6), 16);
            // If all RGB values are > 240, it's essentially white
            if (r > 240 && g > 240 && b > 240) {
                return "#9CA3AF"; // Use gray for white/unknown
            }
            return `#${hex}`;
        }
        return "#9CA3AF";
    };

    const capitalize = (s) => (s && typeof s === 'string' && s.length > 0) ? s.charAt(0).toUpperCase() + s.slice(1) : "";

    const perfRes = user?.performanceResidency || { performance: "", residency: "", color: "" };
    const performanceColor = normalizeColor(perfRes?.color);
    const performanceText = `${capitalize(perfRes?.performance)}-${capitalize(perfRes?.residency)}`;

    const [createNotification] = useMutation(CREATE_NEW_WEB_NOTIFICATION);

    const resetNotificationState = () => {
        setShowNotificationModal(false);
        setNotificationSent(false);
        setSendingNotification(false);
        setNotifyUser(true);
        setNotifyManager(false);
        setUserTitle("");
        setUserDescription("");
        setManagerTitle("");
        setManagerDescription("");
    };

    const handleSendNotification = async () => {
        const managerIdFromState = state.managerId || state.reportingId || null;
        const managerIdFromProfile = profileData?.userProfile?.reporting?.id || null;
        const managerId = managerIdFromState || managerIdFromProfile || null;

        if (!notifyUser && !notifyManager) {
            message.warning("Select at least one recipient");
            return;
        }

        const notifications = [];

        if (notifyUser) {
            if (!userTitle.trim() || !userDescription.trim()) {
                message.warning("Enter title and description for user");
                return;
            }
            notifications.push({
                userId: Number(userId),
                title: userTitle.trim(),
                notificationType: "WEBADMIN",
                referenceId: 0,
                description: userDescription.trim(),
            });
        }

        if (notifyManager) {
            if (!managerId) {
                message.error("Manager not available for this user");
                return;
            }
            if (!managerTitle.trim() || !managerDescription.trim()) {
                message.warning("Enter title and description for manager");
                return;
            }
            notifications.push({
                userId: Number(managerId),
                title: managerTitle.trim(),
                notificationType: "WEBADMIN",
                referenceId: 0,
                description: managerDescription.trim(),
            });
        }

        setSendingNotification(true);
        try {
            const res = await createNotification({
                variables: {
                    data: notifications,
                },
            });

            if (res.data?.createNewWebNotification?.success) {
                setNotificationSent(true);
                message.success("Notification sent");
                setTimeout(() => {
                    resetNotificationState();
                }, 2000);
            } else {
                message.error(res.data?.createNewWebNotification?.message || "Failed to send notification");
                setSendingNotification(false);
            }
        } catch (error) {
            console.error(error);
            message.error("Failed to send notification");
            setSendingNotification(false);
        }
    };

    const openNotificationModal = () => {
        setNotifyUser(true);
        setNotifyManager(hasManager);
        setUserTitle("");
        setUserDescription("");
        setManagerTitle("");
        setManagerDescription("");
        setNotificationSent(false);
        setSendingNotification(false);
        setShowNotificationModal(true);
    };

    return (
        <div>

            <div className="flex justify-between items-center mb-8 -mt-4">
                <button
                    onClick={() => navigate(-1, { state: { name, accountName, accountId } })}
                    className="flex items-center gap-2 bg-[#C21A17] text-white px-5 py-2 ml-4 top-4 rounded-lg shadow-md hover:bg-[#a81412] transition-all"
                >
                    <img
                        src={backIcon}
                        alt="Back"
                        className="w-4 h-4"
                    />
                    <span className="font-medium">Back</span>
                </button>

                <button
                    onClick={() => navigate("/admin")}
                    className="flex items-center gap-2 bg-[#C21A17] text-white px-5 py-2 mr-4 rounded-lg shadow-md hover:bg-[#a81412] transition-all"
                >
                    <img
                        src={homeIcon}
                        alt="Home"
                        className="w-4 h-4"
                    />
                    <span className="font-medium">Home</span>
                </button>
            </div>
            <div className="user-profile-container">
                {/* Header */}
                <h2 className="text-lg font-semibold ml-2">
                    User Understand Profile
                </h2>



                {/* User Card */}
                <div className="bg-white rounded-xl shadow-md border border-[#ECECEC] px-3 py-3 mb-6 w-[1243px] h-[114px]">
                    <div className="flex items-center">

                    {/* Profile Image */}
                    <img
                        src={user.profilePicture}
                        alt={user.name}
                        className="w-[88px] h-[89px] rounded-full object-cover border border-gray-300 flex-shrink-0"
                    />

                    {/* User Details */}
                    <div className="flex-1 ml-4">

                        {/* Name */}
                        <h2 className="text-[16px] font-semibold text-[#393939] mb-2 mt-1">
                            {user.name}
                        </h2>

                        {/* Details */}
                        <div className="grid grid-cols-5 gap-8">

                            {/* Age */}
                            <div>
                                <p className="text-[12px] text-gray-500 mb-0.5">
                                    Age
                                </p>
                                <p className="text-[12px] font-semibold text-#888888-900">
                                    {age}
                                </p>
                            </div>

                            {/* Tenure */}
                            <div>
                                <p className="text-[12px] text-gray-500 mb-0.5">
                                    Tenure
                                </p>
                                <p className="text-[12px] font-semibold text-#888888-900">
                                    {tenure}
                                </p>
                            </div>

                            {/* Manager */}
                            <div>
                                <p className="text-[12px] text-gray-500 mb-0.5">
                                    Manager
                                </p>
                                <p className="text-[12px] font-semibold text-#888888-900">
                                    {managerName}
                                </p>
                            </div>

                            {/* Employee ID */}
                            <div>
                                <p className="text-[12px] text-gray-500 mb-0.5">
                                    Employee ID
                                </p>
                                <p className="text-[12px] font-semibold text-#888888-900">
                                    {employeeId || ""}
                                </p>
                            </div>

                            {/* Performance - Residency */}
                            <div>
                                <p className="text-[12px] text-gray-500 mb-1">
                                    Performance - Residency
                                </p>

                                <span
                                    className="inline-flex items-center justify-center px-3 py-0.5 rounded-full text-[9px] font-semibold text-white"
                                    style={{
                                        background: "#9CA3AF",
                                        minWidth: "100px",
                                    }}
                                >
                                    {performanceText}
                                </span>
                            </div>

                        </div>
                    </div>
                </div>
                </div>

                {/* Preferences & Device */}
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div className="bg-white rounded-2xl shadow-md border border-[#ECECEC] p-3">
                        <h3 className="text-[16px] font-bold text-#393939-900 mb-2 flex items-center gap-2">
                            {/* <Globe className="w-5 h-5 text-[#AD0101]" /> */}
                            Language Preferences
                        </h3>
                        <div className="space-y-2">
                            <div>
                                <p className="text-[14px] text-gray-500">App Language</p>
                                <p className="text-[14px] font-semibold text-gray-900">{appLanguageName}</p>
                            </div>
                            <div>
                                <p className="text-[14px]  text-gray-500">Video Language</p>
                                <p className="text-[14px] font-semibold text-gray-900">{videoLanguage}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-md border border-[#ECECEC] p-4">
                        <h3 className="text-[16px] font-bold text-#393939-900 mb-4 flex items-center gap-2">
                            {/* <Smartphone className="w-5 h-5 text-[#AD0101]" /> */}
                            Device Details
                        </h3>
                        <div className="grid grid-cols-2 gap-6">
                            <div>
                                <p className="text-[14px] text-gray-500">Device Type</p>
                                <p className="text-[14px] font-semibold text-gray-900">{deviceData?.getUserDeviceDetails?.os || ""}</p>
                            </div>
                            <div>
                                <p className="text-[14px] text-gray-500">Model</p>
                                <p className="text-[14px] font-semibold text-gray-900">{deviceData?.getUserDeviceDetails?.deviceModel || ""}</p>
                            </div>
                            <div>
                                <p className="text-[14px] text-gray-500">Device Name</p>
                                <p className="text-[14px] font-semibold text-gray-900">{deviceData?.getUserDeviceDetails?.deviceName || ""}</p>
                            </div>
                            <div>
                                <p className="text-[14px] text-gray-500">OS Version</p>
                                <p className="text-[14px] font-semibold text-gray-900">{deviceData?.getUserDeviceDetails?.osVersion || ""}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Behavioural Insights */}
                <div className="bg-white rounded-2xl shadow-md border border-[#ECECEC] p-4">
                    <div className="flex items-center justify-between ">
                        <h3 className="text-[16px] font-bold text-#393939-900 flex items-center gap-2">
                            Behavioural Insights
                        </h3>
                        {/* <button
                            onClick={openNotificationModal}
                            className="flex items-center gap-2 bg-[#AD0101] text-white px-5 py-2.5 rounded-lg hover:bg-[#b00000] transition-colors font-medium"
                        >
                            Send Notification
                        </button> */}
                    </div>

                    {insightsLoading ? (
                        <div className="text-center py-12">
                            <div className="inline-block w-8 h-8 border-4 border-gray-300 border-t-[#AD0101] rounded-full animate-spin"></div>
                            <p className="mt-3 text-gray-600">Loading insights...</p>
                        </div>
                    ) : insightsError ? (
                        <div className="text-center py-12 text-red-600">
                            Failed to load insights. Please try again.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b-2 border-gray-200">
                                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Question</th>
                                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">User's Answer</th>
                                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">What it Conveys</th>
                                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Recommended Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {user.behaviouralInsights.length === 0 ? (
                                        <tr>
                                            <td colSpan={4} className="text-center py-12 text-gray-500">
                                                No behavioral insights available
                                            </td>
                                        </tr>
                                    ) : (
                                        user.behaviouralInsights.map((insight, i) => (
                                            <tr key={i} className="border-b hover:bg-gray-50 transition-colors">
                                                <td className="py-4 px-4 text-sm font-medium text-gray-900">{insight.question}</td>
                                                <td className="py-4 px-4 text-sm text-gray-700">{insight.answer}</td>
                                                <td className="py-4 px-4 text-sm text-gray-600">{insight.whatItConveys}</td>
                                                <td className="py-4 px-4">
                                                    <span className="inline-block px-3 py-1.5 bg-red-100 text-[#AD0101] rounded-full text-xs font-medium">
                                                        {insight.recommendedAction}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Notification Modal */}
                {showNotificationModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full p-6">
                            {!notificationSent ? (
                                <>
                                    <h3 className="text-xl font-bold text-gray-900 mb-4">Send Notification</h3>
                                    <p className="text-sm text-gray-600 mb-4">
                                        {/* Select who should receive the notification for <strong>{user.name}</strong>. */}
                                    </p>

                                    <div className="flex flex-wrap gap-3 mb-4">
                                        <label className="flex-1 flex items-center p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                                            <input
                                                type="checkbox"
                                                checked={notifyUser}
                                                onChange={(e) => setNotifyUser(e.target.checked)}
                                                className="w-5 h-5 accent-[#AD0101] cursor-pointer"
                                                style={{ accentColor: '#AD0101' }}
                                            />
                                            <span className="ml-3 text-sm font-medium text-gray-900">
                                                {`Send to ${user.name} (User)`}
                                            </span>
                                        </label>

                                        {hasManager && (
                                            <label className="flex-1 flex items-center p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                                                <input
                                                    type="checkbox"
                                                    checked={notifyManager}
                                                    onChange={(e) => setNotifyManager(e.target.checked)}
                                                    className="w-5 h-5 accent-[#AD0101] cursor-pointer"
                                                    style={{ accentColor: '#AD0101' }}
                                                />
                                                <span className="ml-3 text-sm font-medium text-gray-900">
                                                    {`Send to ${managerName} (Manager)`}
                                                </span>
                                            </label>
                                        )}
                                    </div>

                                    {(notifyUser || (notifyManager && hasManager)) && (
                                        <div className="flex flex-wrap gap-4 mb-4">
                                            {notifyUser && (
                                                <div className="flex-1 md:w-1/2 border border-gray-200 rounded-lg p-4">
                                                    <h4 className="text-sm font-semibold text-gray-900 mb-2">Message to {user.name}</h4>
                                                    <input
                                                        type="text"
                                                        value={userTitle}
                                                        onChange={(e) => setUserTitle(e.target.value)}
                                                        placeholder="Title for user"
                                                        className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#AD0101]"
                                                    />
                                                    <textarea
                                                        value={userDescription}
                                                        onChange={(e) => setUserDescription(e.target.value)}
                                                        placeholder="Description for user"
                                                        rows={3}
                                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-[#AD0101]"
                                                    />
                                                </div>
                                            )}

                                            {notifyManager && hasManager && (
                                                <div className="flex-1 border border-gray-200 rounded-lg p-4">
                                                    <h4 className="text-sm font-semibold text-gray-900 mb-2">Message to {managerName}</h4>
                                                    <input
                                                        type="text"
                                                        value={managerTitle}
                                                        onChange={(e) => setManagerTitle(e.target.value)}
                                                        placeholder="Title for manager"
                                                        className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#AD0101]"
                                                    />
                                                    <textarea
                                                        value={managerDescription}
                                                        onChange={(e) => setManagerDescription(e.target.value)}
                                                        placeholder="Description for manager"
                                                        rows={3}
                                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-[#AD0101]"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <div className="flex gap-3">
                                        <button
                                            onClick={resetNotificationState}
                                            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleSendNotification}
                                            disabled={
                                                sendingNotification ||
                                                (!notifyUser && !notifyManager) ||
                                                (notifyUser && (!userTitle.trim() || !userDescription.trim())) ||
                                                (notifyManager && (!managerTitle.trim() || !managerDescription.trim()))
                                            }
                                            className="flex-1 px-4 py-2 bg-[#AD0101] text-white rounded-lg hover:bg-[#b00000] font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                        >
                                            {sendingNotification ? (
                                                <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                                            ) : (
                                                "Send Notification"
                                            )}
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <div className="text-center py-8">
                                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                                        </svg>
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-2">Notification Sent!</h3>
                                    <p className="text-sm text-gray-600">
                                        {notifyUser && notifyManager && "Sent to user and manager"}
                                        {notifyUser && !notifyManager && `Sent to ${user.name}`}
                                        {!notifyUser && notifyManager && `Sent to ${managerName}`}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ClanCrewUnderstandUserProfile;